/**
 * Identity review — every pure decision the screen makes (cca-yb68, ADR-064).
 *
 * The screen itself only renders and moves focus; the words, the ordering, the
 * filter counts, the NZ day arithmetic and the classification of every server
 * outcome live here, where they can be tested without a DOM. vetting-web's
 * tests run in node with no DOM library, so nothing in this file may touch
 * `document`, `window` or React.
 *
 * The server (packages/cca-parishes-worker/src/vetting/identity-review.ts) is
 * the authority on WHAT differs: it returns per-candidate facet codes and hint
 * codes and deliberately never a CRM date of birth. This module owns only the
 * WORDING of those codes (Miranda's §5 copy) and never infers a difference the
 * server did not state.
 */

// ── The server's shapes (mirrored, not imported: separate build graphs) ──────

export type DobFacet = 'same' | 'different' | 'blank_in_crm';
export type NameFacet = 'same' | 'different';

export interface CandidateFacets {
  dob: DobFacet;
  given: NameFacet;
  family: NameFacet;
}

export type HintCode =
  | 'crm_dob_blank'
  | 'possible_changed_surname'
  | 'possible_preferred_name'
  | 'different_person_same_mailbox'
  | 'duplicate_contacts'
  | 'too_many_matches';

export type HeldReasonCode =
  'several_people' | 'no_dob_on_record' | 'name_and_dob_differ' | 'dob_differs' | 'name_differs';

/**
 * The typed side of the comparison — what the applicant (or the registrar,
 * transcribing paper) actually wrote. Never rewritten from CRM (ruling 2).
 * The queue payload and the paper form both supply exactly this much.
 */
export interface TypedApplication {
  applicantName: string;
  applicantEmail: string;
  dateOfBirthSubmitted: string;
  jobTitle: string;
  townCity: string;
}

/** One row of GET /registrar/match-queue. */
export interface ReviewItem extends TypedApplication {
  id: string;
  parishSlug: string;
  reason: string;
  reasonCode: HeldReasonCode;
  reasonLabel: string;
  hints: HintCode[];
  /** Held before e-signatures were captured at submission; resolve captures it. */
  legacy: boolean;
  createdAt: string;
}

export interface ReviewQueueResponse {
  count: number;
  total: number;
  items: ReviewItem[];
  hasMore: boolean;
  nextCursor: string | null;
}

/** One person in CRM on this application's email. Never carries a CRM date of
 *  birth — only the `dob` facet says whether it agreed. */
export interface ReviewCandidate {
  contactId: string;
  name: string;
  church: string | null;
  townCity: string | null;
  facets: CandidateFacets;
  hints: HintCode[];
  applications: {
    count: number;
    recent: {
      status: string | null;
      parishName: string | null;
      jobTitle: string | null;
      submittedAt: string | null;
    }[];
  } | null;
}

export interface CandidatesPayload {
  quarantineId?: string;
  candidates: ReviewCandidate[];
  moreCandidates: boolean;
  hints: HintCode[];
  reason: HeldReasonCode | null;
  version: string;
}

/**
 * A 409 that refuses AND re-states the evidence: the candidates payload with
 * the refusal code and its sentence spread over it. Both routes answer in this
 * one shape — the registrar's resolve (`record_changed`,
 * `registrar-dashboard.ts:1282-1289`) and the paper route
 * (`identity_review_required` and `record_changed`,
 * `parish-dashboard.ts:592-613`) — so the screen can show the CURRENT people
 * on record in the same breath as the refusal.
 */
export interface IdentityRefusal extends CandidatesPayload {
  error: string;
  message?: string;
}

// ── Words for codes ─────────────────────────────────────────────────────────

/** Queue and filter words. Internal codes never reach a screen (Miranda §3d). */
export const REASON_LABELS: Record<HeldReasonCode, string> = {
  name_differs: 'Name differs',
  dob_differs: 'Date of birth differs',
  name_and_dob_differ: 'Name and date of birth differ',
  no_dob_on_record: 'No date of birth on record',
  several_people: 'Several people use this email',
};

/** The fixed filter order — never sorted by count, so the list of choices does
 *  not move under the registrar between one action and the next. */
export const REASON_ORDER: HeldReasonCode[] = [
  'name_differs',
  'dob_differs',
  'name_and_dob_differ',
  'no_dob_on_record',
  'several_people',
];

/**
 * Hints in plain words. The server's six codes are the ONLY hints shown: it
 * returns no CRM date of birth, so the date arithmetic Miranda sketched ("day
 * and month swapped", "{n} years apart") and "matches a previous name they
 * gave" cannot be computed here and are deliberately absent.
 */
export const HINT_SENTENCES: Record<HintCode, string> = {
  crm_dob_blank: 'The person on record has no date of birth, so they cannot be confirmed.',
  possible_changed_surname:
    'Same date of birth and given name — this may be the same person under a changed family name.',
  possible_preferred_name:
    'Same date of birth and family name — this may be the same person under a preferred or shortened given name.',
  different_person_same_mailbox:
    'A different date of birth — this is likely another person using the same mailbox.',
  duplicate_contacts: 'Two people on record agree on every detail — likely duplicate CRM records.',
  too_many_matches: 'Too many people use this email to list them all.',
};

/** A facet, as a glyph and a word — state is never carried by colour alone
 *  (Miranda §2.5). The glyph is decorative; the word is the accessible text. */
export const FACET_WORDS: Record<DobFacet, { glyph: string; word: string }> = {
  same: { glyph: '=', word: 'Same' },
  different: { glyph: '≠', word: 'Different' },
  blank_in_crm: { glyph: '–', word: 'Not on record' },
};

/** `.pill-*` class for a facet. Same is deliberately neutral, not success
 *  green: green reads as "go" and pushes the registrar toward Link. */
export function facetPillClass(facet: DobFacet): 'pill-neutral' | 'pill-warn' {
  return facet === 'same' ? 'pill-neutral' : 'pill-warn';
}

export function hintSentences(hints: readonly HintCode[]): string[] {
  return hints.map((h) => HINT_SENTENCES[h]).filter((s): s is string => Boolean(s));
}

// ── NZ dates (UX-8: every day boundary is Pacific/Auckland) ─────────────────

const NZ_DAY = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function nzCalendarDay(d: Date): number {
  const parts = Object.fromEntries(NZ_DAY.formatToParts(d).map((p) => [p.type, p.value]));
  return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
}

/** Whole NZ calendar days an item has waited (0 = received today, NZ time).
 *  Null for an unparseable timestamp — never a fabricated zero. */
export function nzDaysWaiting(createdAtIso: string, now: Date = new Date()): number | null {
  const from = new Date(createdAtIso);
  if (isNaN(from.getTime())) return null;
  return Math.max(0, Math.round((nzCalendarDay(now) - nzCalendarDay(from)) / 86_400_000));
}

/** "8 Sep 2026" in NZ time, or an em dash for a missing/unparseable value. */
export function fmtNzDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * A typed date of birth, day/month/year as New Zealand writes it. Pure string
 * surgery on the stored `YYYY-MM-DD`: no Date, no clock and no timezone, so a
 * birthday can never shift by a day on its way to the screen. Anything not in
 * that shape is shown exactly as it was typed.
 */
export function fmtTypedDob(raw: string): string {
  const s = (raw ?? '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return s === '' ? '—' : s;
  const [, year = '', month = '', day = ''] = m;
  return `${day}/${month}/${year}`;
}

/** "Mar 2023" — the month of an other-application line. */
export function fmtNzMonth(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    month: 'short',
    year: 'numeric',
  });
}

/** "12 waiting · oldest 9 days" / "1 waiting · received today". */
export function metaLine(total: number, oldestCreatedAt: string | null, now: Date = new Date()) {
  if (total === 0) return '';
  const waiting = `${total} waiting`;
  const days = oldestCreatedAt === null ? null : nzDaysWaiting(oldestCreatedAt, now);
  if (days === null) return waiting;
  if (days === 0) return `${waiting} · received today`;
  return `${waiting} · oldest ${days} ${days === 1 ? 'day' : 'days'}`;
}

/** A waiting phrase for one row: "waiting 9 days" / "received today". */
export function waitingPhrase(createdAtIso: string, now: Date = new Date()): string {
  const days = nzDaysWaiting(createdAtIso, now);
  if (days === null) return `received ${fmtNzDate(createdAtIso)}`;
  if (days === 0) return 'received today';
  return `waiting ${days} ${days === 1 ? 'day' : 'days'}`;
}

// ── The list: honesty about what is shown, the filter, the ordering ─────────

/**
 * UX-7: a list the server pages says so. The server answers `total` through
 * the same predicate as the stats pill, so `loaded < total` is a page boundary,
 * never a silent cap.
 */
export function showingLine(loaded: number, total: number): string | null {
  if (loaded >= total) return null;
  return `Showing the oldest ${loaded} of ${total}. Load more to see the rest.`;
}

export interface FilterOption {
  value: 'all' | HeldReasonCode;
  label: string;
  count: number;
}

/** Filter choices with counts over the items actually LOADED — the label says
 *  so when the list is still paging, so a count can never claim to describe
 *  rows nobody has fetched. Reasons with no loaded items are dropped. */
export function filterOptions(items: readonly ReviewItem[], total: number): FilterOption[] {
  const counted = REASON_ORDER.flatMap((code): FilterOption[] => {
    const count = items.filter((i) => i.reasonCode === code).length;
    return count === 0 ? [] : [{ value: code, label: `${REASON_LABELS[code]} (${count})`, count }];
  });
  const loaded = items.length;
  const allLabel =
    loaded < total ? `All reasons (${loaded} of ${total} loaded)` : `All reasons (${loaded})`;
  return [{ value: 'all', label: allLabel, count: loaded }, ...counted];
}

export function applyFilter(
  items: readonly ReviewItem[],
  filter: 'all' | HeldReasonCode,
): ReviewItem[] {
  return filter === 'all' ? [...items] : items.filter((i) => i.reasonCode === filter);
}

/** The accessible name of a collapsed row's disclosure button (Miranda §5). */
export function rowSummary(item: ReviewItem, parishName: string, now: Date = new Date()): string {
  return [item.applicantName, parishName, item.reasonLabel, waitingPhrase(item.createdAt, now)]
    .filter(Boolean)
    .join(', ');
}

/** "{n} more from this email" — held items that share this mailbox. Null when
 *  this is the only one, so the row stays quiet in the common case. */
export function siblingNote(items: readonly ReviewItem[], item: ReviewItem): string | null {
  const email = item.applicantEmail.trim().toLowerCase();
  if (!email || email === '—') return null;
  const n = items.filter(
    (i) => i.id !== item.id && i.applicantEmail.trim().toLowerCase() === email,
  ).length;
  return n === 0 ? null : `${n} more from this email`;
}

/** A parish NAME for a slug (the queue carries the slug only). Falls back to
 *  the slug so a parish missing from the list is still identifiable. */
export function parishNameFor(
  slug: string,
  parishes: readonly { slug: string; name: string }[],
): string {
  return parishes.find((p) => p.slug === slug)?.name ?? slug;
}

const agreement = (f: CandidateFacets): number =>
  (f.dob === 'same' ? 1 : 0) + (f.given === 'same' ? 1 : 0) + (f.family === 'same' ? 1 : 0);

/**
 * Display order for the several-people cards: most agreeing fields first, ties
 * to the oldest CRM record (Zoho ids grow, so the shorter/lower id is older).
 * The server orders the same way; this keeps the order stable if a payload
 * ever arrives unordered, and is the order the radio cards are announced in.
 */
export function orderCandidatesForDisplay(
  candidates: readonly ReviewCandidate[],
): ReviewCandidate[] {
  return [...candidates].sort(
    (a, b) =>
      agreement(b.facets) - agreement(a.facets) ||
      a.contactId.length - b.contactId.length ||
      a.contactId.localeCompare(b.contactId),
  );
}

// ── The review card ─────────────────────────────────────────────────────────

/** The agreement strip: which of the three agreed. Email is always Same — the
 *  email IS how the candidate was found. */
export function agreementStrip(facets: CandidateFacets): { label: string; facet: DobFacet }[] {
  const name: NameFacet =
    facets.given === 'same' && facets.family === 'same' ? 'same' : 'different';
  return [
    { label: 'Email', facet: 'same' },
    { label: 'Date of birth', facet: facets.dob },
    { label: 'Name', facet: name },
  ];
}

/** A comparison row. `onRecord` is null where the server gives no CRM value —
 *  the date of birth above all: the facet says Same/Different/Not on record
 *  and no CRM date is ever sent to the browser. */
export interface ComparisonRow {
  label: string;
  typed: string;
  onRecord: string | null;
  facet: DobFacet | null;
  /** True when the row is one value that belongs to both sides (the email). */
  shared?: boolean;
}

const presentOr = (v: string | null | undefined, fallback: string): string => {
  const s = (v ?? '').trim();
  return s === '' || s === '—' ? fallback : s;
};

/**
 * The typed-left / CRM-right comparison.
 *
 * Both sides' names arrive joined (the queue payload carries `applicantName`,
 * the candidate a CRM display name), so the name is ONE row with the two facet
 * pills beside it rather than Miranda's separate given/family rows — neither
 * side can be split without guessing where a multi-word family name begins.
 */
export function comparisonRows(
  item: TypedApplication,
  candidate: ReviewCandidate | null,
  parishName: string,
): ComparisonRow[] {
  const nameFacet: NameFacet | null = candidate
    ? candidate.facets.given === 'same' && candidate.facets.family === 'same'
      ? 'same'
      : 'different'
    : null;
  return [
    {
      label: 'Name',
      typed: presentOr(item.applicantName, '—'),
      onRecord: candidate ? presentOr(candidate.name, 'Not on record') : null,
      facet: nameFacet,
    },
    {
      label: 'Date of birth',
      typed: fmtTypedDob(presentOr(item.dateOfBirthSubmitted, '—')),
      // Registrar-only screen, but the server sends no CRM date of birth at
      // all: the facet is the whole answer.
      onRecord: null,
      facet: candidate ? candidate.facets.dob : null,
    },
    {
      label: 'Email',
      typed: presentOr(item.applicantEmail, '—'),
      onRecord: null,
      facet: candidate ? 'same' : null,
      shared: true,
    },
    {
      label: 'Parish',
      typed: parishName,
      onRecord: candidate ? presentOr(candidate.church, 'No church on record') : null,
      facet: null,
    },
    {
      label: 'Town or city',
      typed: presentOr(item.townCity, '—'),
      onRecord: candidate ? presentOr(candidate.townCity, 'Not on record') : null,
      facet: null,
    },
    {
      label: 'Role applied for',
      typed: presentOr(item.jobTitle, '—'),
      onRecord: null,
      facet: null,
    },
  ];
}

/** Per-field name pills, spelled out for a screen reader. */
export function namePills(facets: CandidateFacets): { label: string; facet: NameFacet }[] {
  return [
    { label: 'Given name(s)', facet: facets.given },
    { label: 'Family name', facet: facets.family },
  ];
}

/** "Youth leader · St Other's · Result Received Mar 2023". Status words only —
 *  never a vetting OUTCOME (the server's summary carries none). */
export function applicationLine(a: {
  status: string | null;
  parishName: string | null;
  jobTitle: string | null;
  submittedAt: string | null;
}): string {
  const when = fmtNzMonth(a.submittedAt);
  return [a.jobTitle, a.parishName, [a.status, when].filter(Boolean).join(' ')]
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(' · ');
}

/**
 * "Other applications on this CRM record (3)" + an "and {n} more" tail.
 *
 * "Other" is the server's word, not a filter applied here: the move routes pass
 * `excludeRecordId` so the application on screen is not counted on the Contact
 * it is currently linked to (Miranda S-2). Do not re-filter by id here — the
 * payload carries no record ids, by design (PII-minimal, ≤5 candidates).
 */
export function otherApplications(candidate: ReviewCandidate): {
  heading: string;
  lines: string[];
  more: string | null;
} | null {
  if (!candidate.applications) return null;
  const { count, recent } = candidate.applications;
  if (count === 0) return null;
  const lines = recent.map(applicationLine).filter(Boolean);
  const hidden = count - lines.length;
  return {
    heading: `Other applications on this CRM record (${count})`,
    lines,
    more: hidden > 0 ? `and ${hidden} more` : null,
  };
}

// ── The two answers, and what pressing them says ────────────────────────────

export const QUESTION = 'Is this the same person?';

/** Two EQUAL answers, always in this order, neither of them primary and
 *  neither preselected: the incident this stream fixes was a silent default. */
export function answerLabels(crmName: string | null): { link: string; create: string } {
  return {
    link: crmName ? `Yes — link to ${crmName}` : 'Yes — link to the chosen person',
    create: crmName ? 'No — create a new person' : 'No — none of these, create a new person',
  };
}

export const BUSY_LABELS = { link: 'Linking…', create: 'Creating…' } as const;

/**
 * The way OUT of the chooser, offered only where the chooser is a disclosure
 * the registrar can have opened by mistake (the R1 move). It is NOT a third
 * answer: it decides nothing, so it sits after the two answers and never reads
 * as a recommendation. It is also not the toggle that opened the panel, so
 * label-in-name (SC 2.5.3) is untouched.
 */
export const LEAVE_AS_IS = 'Leave it as it is';

export const CHOOSE_FIRST = 'Choose which person first.';

export const SEVERAL_PEOPLE_LEGEND = (n: number): string =>
  `Several people in CRM use this email (${n}). Which person is it?`;

export const MORE_PEOPLE_NOTE = 'More people use this email than are shown here — search CRM.';

export const PLACEHOLDER_COLUMN = 'Choose a person above';

/**
 * Marks the person the application is on RIGHT NOW. The server has always sent
 * `currentContactId`; until this round the chooser dropped it, so a registrar
 * moving a link was asked to choose between names with nothing on screen saying
 * which one he was choosing away FROM.
 */
export const LINKED_NOW = 'Linked now';

/**
 * The single-candidate case where the only person on the mailbox is the person
 * the application is ALREADY on. Offering "Yes, move to Ann Example" there is a
 * no-op dressed as a decision, behind a four-sentence confirmation: say so
 * instead, and leave Create as the only real answer.
 */
export const alreadyLinkedNote = (crmName: string): string =>
  `This application is already linked to ${crmName}, and nobody else is on record for that email ` +
  `address. If it belongs to somebody new, create a new person.`;

/**
 * The inline Link confirmation. Friction goes where a third party can be
 * harmed (SET-4): Police results would be filed on that person's record.
 *
 * Every sentence here describes something the built worker ACTUALLY does, and
 * nothing it does not:
 * - R1 (moving a link afterwards) IS built now, so the last line says so, and
 *   says where it stops. It used to read "This can't be undone from the
 *   dashboard", which was true when it was written and became false the moment
 *   `POST …/record/:id/relink` shipped. A confirmation that overstates how
 *   final a choice is buys the wrong kind of care: it makes a registrar who
 *   has spotted a mistake believe the dashboard cannot help him.
 * - The typed name does NOT yet survive onto the other surfaces (ADR-064 §8 is
 *   Stream B): the parish queue, the printed application and its signature
 *   line, the official form PDF, the Drive file names and the NZPV lodgement
 *   CSV all still read the LINKED Contact's name. So the paragraph states the
 *   consequence as a temporary warning rather than the comfort it used to give
 *   ("The application keeps the name the applicant typed"), which was false on
 *   every one of those surfaces.
 * - The applicant is NOT emailed when the registrar resolves an ordinary held
 *   item (they already had the receipt when it was held — the resolve passes
 *   `applicantAlreadyConfirmed`), so only the parish approvers are named. That
 *   sentence is true on the legacy path too, where the applicant IS also
 *   emailed: the approvers are emailed either way.
 */
export function linkConfirmText(a: { crmName: string; typedName: string; parishName: string }): {
  heading: string;
  /** A single sentence shown ABOVE the body, in its own emphasised block. Only
   *  the MOVE carries one (the registrar's ruling of 2026-09-18); a first link
   *  has nothing that can already be at Police. */
  warn?: string;
  body: string[];
  confirm: string;
  back: string;
} {
  return {
    heading: `Link to ${a.crmName}?`,
    body: [
      `Police results for this application will be filed on ${a.crmName}'s CRM record. Nothing on that record is changed now.`,
      `Until the typed-name change ships, the printed form and the NZPV lodgement will show ${a.crmName}, not ${a.typedName}.`,
      `It goes to ${a.parishName} for approval, and the parish approvers are emailed.`,
      'You can move it to a different person until it is lodged with Police.',
    ],
    confirm: `Yes, link to ${a.crmName}`,
    back: 'Back',
  };
}

/** The shape of an inline Link confirmation — `linkConfirmText` for a first
 *  link, `relinkConfirmText` for a move. The chooser takes whichever applies. */
export type LinkConfirmCopy = typeof linkConfirmText;

// ── R1: moving a link to another person, until lodgement ───────────────────

export const RELINK = {
  /** A discrete link, never a button beside the primary action (Miranda §3b,
   *  the discrete-interface rule): moving a link is a correction, not part of
   *  the everyday lodge-and-record flow.
   *
   *  The label does NOT change to "Close" when the panel opens. A disclosure
   *  whose visible text changes while its accessible name names the applicant
   *  breaks label-in-name (SC 2.5.3) — a speech-input user says what they can
   *  see. `aria-expanded` carries the open/closed state instead, which is what
   *  it is for. */
  open: 'Move to another person',
  heading: 'Who does this application belong to?',
  body:
    'Moving it changes only which CRM person the Police result will be filed on. ' +
    "The application keeps its own details, and nothing is written onto either person's record.",
  loadError: "Couldn't load the people on record for this application.",
  /** Why the move is not offered on a lodged row. Not a disabled control: a
   *  disabled control gives no reason (Miranda §3c). */
  locked:
    'Lodged with Police, so it can no longer be moved to another person — the result will be ' +
    'filed on the person it is linked to now.',
} as const;

/**
 * The accessible name of the discrete toggle. Three of these can be open on one
 * screen — the lodgement queue is a list — and "Move to another person" three
 * times over tells a screen-reader user nothing about WHICH application. The
 * visible label is kept as a whole substring so label-in-name still holds
 * (SC 2.5.3).
 */
export const relinkToggleLabel = (applicantName: string): string =>
  `${RELINK.open} — ${applicantName}`;

/**
 * The inline confirmation for a MOVE. Same shape as `linkConfirmText`, so the
 * one chooser renders either; different sentences, because a move does
 * different things:
 *  - the application is already with its parish, so nobody is emailed and it
 *    does not "go to the parish for approval";
 *  - the Drive folder follows the person, but only if one exists yet;
 *  - and the window closes at lodgement, which is the whole reason this
 *    affordance is safe to offer at all (ADR-064 §9).
 *
 * `warn` is the REGISTRAR'S OWN RULING of 2026-09-18, and it is unconditional.
 * The lock keys on the record's Blueprint status, and that status only moves
 * when he presses "Mark lodged" — which he does at the END of an evening of
 * keying applications into the NZPV portal. So between the act of lodging and
 * the fact of it the screen still offers the move, in good faith, and no code
 * here can tell. He was offered a stamped export and a merged download-and-lodge
 * button and declined both: the answer is to SAY it, every time, above the
 * body, where it cannot be read as a footnote. When Stream B or a stamped
 * export closes the gap, this sentence is re-decided — not quietly dropped.
 *
 * The third body line is the Stream-B warning the Link confirmation already
 * carries (ADR-064 §8), and it matters MORE here: the next control on this row
 * is "Mark lodged", and a move rewrites the name on the printed form, the Drive
 * folder, the parish's card and the NZPV lodgement CSV.
 */
export function relinkConfirmText(a: {
  crmName: string;
  typedName: string;
  parishName: string;
}): ReturnType<LinkConfirmCopy> {
  return {
    heading: `Move to ${a.crmName}?`,
    warn:
      'If you have already keyed this application into the NZPV portal, do not move it — the ' +
      'result will come back against the new person.',
    body: [
      `Police results for this application will be filed on ${a.crmName}'s CRM record instead. Nothing is written onto either person's record.`,
      `The application keeps its own details, including the date of birth and address ${a.typedName} gave.`,
      `Until the typed-name change ships, the printed form, the Drive folder, the parish's list and the NZPV lodgement will all show ${a.crmName}, not ${a.typedName}.`,
      "If the application already has a Drive folder, it moves to the new person's name. Nobody is emailed.",
      'You can move it again until it is lodged with Police. After that it is locked.',
    ],
    confirm: `Yes, move to ${a.crmName}`,
    back: 'Back',
  };
}

export type RelinkOutcomeKind =
  'record_changed' | 'already_lodged' | 'unavailable' | 'not_found' | 'forbidden' | 'failed';

export interface RelinkFailure {
  kind: RelinkOutcomeKind;
  message: string;
  /** The fresh candidates a 409 `record_changed` carries. */
  fresh?: CandidatesPayload;
}

const RELINK_FALLBACKS: Record<'already_lodged' | 'unavailable' | 'not_found', string> = {
  already_lodged: RELINK.locked,
  unavailable:
    'What the applicant typed is no longer on file for this application, so the people on record cannot be looked up.',
  not_found: 'This application is no longer on file. Reload the dashboard.',
};

/**
 * Classify a refusal from either relink route — the candidates GET and the
 * move POST answer in the same codes, so one classifier reads both.
 *
 * The server's own `message` is PREFERRED wherever it sends one: `already_lodged`
 * and `typed_application_unavailable` are the two refusals whose plain words
 * are the registrar's whole answer, and re-wording them here would let the two
 * drift.
 */
export function classifyRelinkError(err: unknown): RelinkFailure {
  const e = (err ?? {}) as ErrorLike;
  const status = e.status;
  const code = e.data?.error;
  const body: unknown = e.data;
  const served = typeof e.data?.message === 'string' ? e.data.message : null;

  if (status === 403 || status === 401) {
    return { kind: 'forbidden', message: RESOLVE_MESSAGES.forbidden };
  }
  if (status === 404) {
    return { kind: 'not_found', message: served ?? RELINK_FALLBACKS.not_found };
  }
  if (status === 409 && code === 'already_lodged') {
    return { kind: 'already_lodged', message: served ?? RELINK_FALLBACKS.already_lodged };
  }
  if (status === 409 && code === 'typed_application_unavailable') {
    return { kind: 'unavailable', message: served ?? RELINK_FALLBACKS.unavailable };
  }
  if (status === 409 && code === 'record_changed') {
    return {
      kind: 'record_changed',
      message: RESOLVE_MESSAGES.record_changed,
      ...(isIdentityRefusal(body) ? { fresh: body } : {}),
    };
  }
  return {
    kind: 'failed',
    message: `Couldn't move this application. ${RESOLVE_MESSAGES.failed}`,
  };
}

/**
 * What a change's notice adds when its audit row could not be written (cca-gmdm).
 * The audit row is the only record of who made the change, and the change itself
 * stands. The worker has already raised the heartbeat counter and a telemetry
 * ERROR row, so this sentence tells the person who just acted, and says no more
 * than is true: the fault has been logged, not fixed, and nothing is asked of
 * them. Used by the relink notice below, and by the parish reassign's and the
 * cancellation's notices (VettingQueuePage). It names no recipient on purpose:
 * the registrar is the one reading the relink and reassign notices, and an
 * approver as well as the registrar reads the cancellation's.
 *
 * ONE sentence on all four surfaces (Miranda r4): the worker's edit page shows
 * it too, typed identically as `AUDIT_NOT_LOGGED_NOTE` in
 * packages/cca-parishes-worker/src/vetting/routes/application-view.ts, because
 * the worker cannot import this package. That package's
 * test/vetting-audit-note-parity.test.ts reads both files and fails when they
 * differ. Change one, change both.
 */
export const AUDIT_NOT_LOGGED_NOTE =
  "The change stands, but the record of who made it could not be saved. You don't need to do anything — the fault has been logged for follow-up.";

/**
 * What the status region says after a move. The Drive outcome is reported when
 * it did NOT simply follow the person, and only then: the folder is the
 * registrar's to tidy, and a silent failure would leave it filed under the
 * wrong person's name.
 *
 * `collision` is the deliberate refusal, not an error (ADR-064 §9): a folder
 * already carries the new person's name — on a shared household mailbox that is
 * usually a RELATIVE's vetting folder — so nothing was moved and nothing was
 * trashed. Merging would have re-parented this applicant's signed consent form,
 * with their date of birth, address and signature, into that person's folder,
 * and moving the link back would not have brought it out. Both folders are
 * intact and the registrar decides in Drive.
 */
export function relinkedMessage(a: {
  action: 'link' | 'create';
  typedName: string;
  crmName?: string | null;
  drive?: string | null;
  /** The worker's `auditLogged` (cca-gmdm): only `false` adds a sentence. */
  auditLogged?: boolean | undefined;
}): string {
  const where =
    a.action === 'link' ? `${a.crmName ?? 'the chosen person'}'s record` : 'a new CRM person';
  const head = `Moved ${a.typedName}'s application to ${where}. Police results will be filed there.`;
  let said = head;
  if (a.drive === 'failed')
    said = `${head} The Drive folder could not be renamed — rename it by hand.`;
  else if (a.drive === 'collision')
    said =
      `${head} The Drive folder was LEFT WHERE IT IS: a folder in the new name already exists in ` +
      `that parish, and nothing was moved into it. Both folders are intact — sort them out in Drive.`;
  return a.auditLogged === false ? `${said} ${AUDIT_NOT_LOGGED_NOTE}` : said;
}

export const DISCARD_CONFIRM = {
  heading: 'Discard this application?',
  body: "It will be taken off the list. The applicant won't be told, and nothing is written to CRM.",
  confirm: 'Yes, discard',
  back: 'Back',
} as const;

/** What the persistent status region says after a success. */
export function successMessage(a: {
  action: 'link' | 'create';
  typedName: string;
  crmName?: string | null;
  parishName: string;
  remaining: number;
}): string {
  const head =
    a.action === 'link'
      ? `Linked ${a.typedName} to ${a.crmName ?? 'the chosen person'}'s record.`
      : `Created a new CRM person for ${a.typedName}.`;
  const tail = a.remaining === 0 ? 'None left waiting.' : `${a.remaining} still waiting.`;
  return `${head} It's now with ${a.parishName} for approval. ${tail}`;
}

export function discardedMessage(typedName: string, remaining: number): string {
  const tail = remaining === 0 ? 'None left waiting.' : `${remaining} still waiting.`;
  return `Discarded ${typedName}'s application. Nothing was written to CRM. ${tail}`;
}

// ── Every server outcome, classified once ───────────────────────────────────

/**
 * The outcomes of POST …/resolve and the paper route's re-POST, as one union.
 * `repeat` (a second press that landed after the first succeeded) is a SUCCESS,
 * not an error: the server answers 200 with the stored outcome.
 */
export type ResolveOutcomeKind =
  | 'succeeded'
  | 'record_changed'
  | 'in_progress'
  | 'already_resolved'
  | 'part_done'
  | 'failed'
  | 'forbidden';

export interface ResolveFailure {
  kind: Exclude<ResolveOutcomeKind, 'succeeded'>;
  message: string;
  /** A fresh candidates payload the 409 record_changed carries. */
  fresh?: CandidatesPayload;
  /** True when a Contact was created before the failure: a retry reuses it. */
  contactCreated?: boolean;
}

interface ErrorLike {
  status?: number;
  data?: {
    error?: string;
    message?: string;
    contactCreated?: boolean;
    candidates?: unknown;
    version?: unknown;
  };
}

const RESOLVE_MESSAGES: Record<Exclude<ResolveOutcomeKind, 'succeeded'>, string> = {
  record_changed:
    'The CRM record changed after you opened this. The people on record have been reloaded — check them and choose again.',
  in_progress:
    'This application is already being dealt with, perhaps in another tab. Reload the list.',
  already_resolved:
    "This application was already dealt with, perhaps in another tab. It's been taken off the list.",
  part_done:
    "A new CRM person was created, but the application couldn't be sent to the parish. Try again — it won't create a second person.",
  failed: 'Nothing has changed — try again.',
  forbidden: 'Your session has expired. Sign in again to carry on.',
};

/** Does this 409 body carry the evidence as well as the refusal? Structural,
 *  and deliberately tolerant about the rest of the payload: a body that has the
 *  candidates, the version and a refusal code can be shown. */
const isIdentityRefusal = (d: unknown): d is IdentityRefusal =>
  Boolean(d) &&
  typeof d === 'object' &&
  Array.isArray((d as { candidates?: unknown }).candidates) &&
  typeof (d as { version?: unknown }).version === 'string' &&
  typeof (d as { error?: unknown }).error === 'string';

/**
 * Classify a thrown API error into exactly one outcome + the words for it.
 * Every documented answer of the built worker is covered: 403 forbidden, 404
 * not_found_or_already_resolved, 409 record_changed (+ fresh payload), 409
 * in_progress, 502 resolve_failed (+ contactCreated), and anything else.
 */
export function classifyResolveError(err: unknown, action: 'link' | 'create'): ResolveFailure {
  const e = (err ?? {}) as ErrorLike;
  const status = e.status;
  const code = e.data?.error;
  // Read the body as unknown and let the guard type it — the refusal bodies
  // carry MORE than the declared error shape, and that surplus is the evidence.
  const body: unknown = e.data;

  if (status === 403 || status === 401) {
    return { kind: 'forbidden', message: RESOLVE_MESSAGES.forbidden };
  }
  if (status === 404) {
    return { kind: 'already_resolved', message: RESOLVE_MESSAGES.already_resolved };
  }
  if (status === 409 && code === 'record_changed') {
    return {
      kind: 'record_changed',
      message: RESOLVE_MESSAGES.record_changed,
      ...(isIdentityRefusal(body) ? { fresh: body } : {}),
    };
  }
  if (status === 409 && code === 'in_progress') {
    return { kind: 'in_progress', message: RESOLVE_MESSAGES.in_progress };
  }
  if (e.data?.contactCreated === true) {
    return { kind: 'part_done', message: RESOLVE_MESSAGES.part_done, contactCreated: true };
  }
  const head =
    action === 'link' ? "Couldn't link this application." : "Couldn't create the new person.";
  return { kind: 'failed', message: `${head} ${RESOLVE_MESSAGES.failed}` };
}

/** The discard route's outcomes: 409 in_progress, 404, or anything else. */
export function classifyDiscardError(err: unknown): ResolveFailure {
  const e = (err ?? {}) as ErrorLike;
  if (e.status === 403 || e.status === 401) {
    return { kind: 'forbidden', message: RESOLVE_MESSAGES.forbidden };
  }
  if (e.status === 409 && e.data?.error === 'in_progress') {
    return { kind: 'in_progress', message: RESOLVE_MESSAGES.in_progress };
  }
  if (e.status === 404) {
    return { kind: 'already_resolved', message: RESOLVE_MESSAGES.already_resolved };
  }
  return {
    kind: 'failed',
    message: `Couldn't discard this application. ${RESOLVE_MESSAGES.failed}`,
  };
}

/** The paper route's 409s: the chooser opens, or the version went stale. Any
 *  other 409 (an ordinary refusal with no evidence) is not ours. */
export function classifyPaperIdentityError(
  err: unknown,
): { kind: 'review_required' | 'record_changed'; payload: CandidatesPayload } | null {
  const e = (err ?? {}) as ErrorLike;
  if (e.status !== 409) return null;
  const body: unknown = e.data;
  if (!isIdentityRefusal(body)) return null;
  if (body.error === 'identity_review_required') return { kind: 'review_required', payload: body };
  if (body.error === 'record_changed') return { kind: 'record_changed', payload: body };
  return null;
}

export const PAPER_REVIEW_HEADING = 'Who does this application belong to?';
/** No "then submit again": choosing IS the submit here (the chooser's own
 *  button re-posts with the decision), and the form's Submit would re-post
 *  without one and earn the same 409. */
export const PAPER_REVIEW_BODY =
  "The details you typed don't all agree with the person already on record for this email, so nothing has been saved yet. Choose who it belongs to.";
export const PAPER_RECORD_CHANGED =
  'The CRM record changed while you were choosing. The people on record have been reloaded — check them and choose again.';

// ── Focus after an action (Miranda §3b) ─────────────────────────────────────

/**
 * Where focus goes once an item leaves the list: the NEXT item's heading
 * button (and that item opens), else the previous one, else the empty-state
 * line. Keyboard order, not screen order — the list is oldest-first, so the
 * next item is the next-oldest, which is the one the registrar is working
 * toward.
 */
/** Why the Link confirmation closed. `back` is the registrar asking to come
 *  back (Back or Escape); `refused` is the server refusing and REPLACING the
 *  evidence (409 `record_changed`), which destroys the button that was pressed. */
export type ConfirmExitReason = 'back' | 'refused';

export interface ConfirmExit {
  reason: ConfirmExitReason;
  /** Is the inline `role="alert"` on the card right now? */
  alertRendered: boolean;
  /** Is the "Yes — link to…" answer on the card right now? (Not on a card with
   *  nobody on record.) */
  yesButtonRendered: boolean;
}

/**
 * Where focus goes when the Link confirmation closes — ONE function owns every
 * exit from the prompt, so no path can quietly end up with nothing focused.
 *
 * - **Refused**, evidence replaced → the inline alert. The registrar pressed a
 *   button that no longer exists, and the alert is both what changed and what
 *   must be read before choosing again.
 * - **Back or Escape** → the Yes button that opened the prompt, so a keyboard
 *   user resumes exactly where they were.
 * - **Neither on the page** → the question heading, the nearest thing that
 *   still names what is being decided.
 *
 * Never "nothing": an unfocused close drops the reader to the top of the
 * dashboard, behind the header and every earlier section. That was measured
 * twice on this screen — first on Back and Escape, then on `record_changed`.
 */
export function confirmCloseFocusTarget(
  exit: ConfirmExit,
): 'error-alert' | 'yes-button' | 'question-heading' {
  if (exit.reason === 'refused' && exit.alertRendered) return 'error-alert';
  if (exit.yesButtonRendered) return 'yes-button';
  return 'question-heading';
}

export function focusAfterResolve(
  visible: readonly ReviewItem[],
  resolvedId: string,
): { kind: 'item'; id: string } | { kind: 'empty' } {
  const i = visible.findIndex((x) => x.id === resolvedId);
  if (i === -1) {
    const first = visible[0];
    return first ? { kind: 'item', id: first.id } : { kind: 'empty' };
  }
  const next = visible[i + 1] ?? visible[i - 1];
  return next ? { kind: 'item', id: next.id } : { kind: 'empty' };
}

export const COPY = {
  heading: 'Identity review',
  loading: 'Loading identity review…',
  empty: 'No applications waiting for identity review.',
  loadError: "Couldn't load the identity review list.",
  tryAgain: 'Try again',
  loadMore: 'Load more',
  loadingMore: 'Loading…',
  reloadList: 'Reload the list',
  filterLabel: 'Show:',
  typedColumn: 'What the applicant typed',
  crmColumn: 'On record in CRM',
  candidatesLoading: 'Loading the people on record…',
  candidatesError: "Couldn't load the people on record for this email.",
  noCandidates:
    'Nobody in CRM uses this email now. Create a new person to send this application to the parish.',
  emailApplicant: 'Email the applicant',
  discard: 'Discard…',
  received: (iso: string) => `Received ${fmtNzDate(iso)}`,
  legacyNote:
    "Held by the older check: this applicant's e-signature is captured when you resolve it.",
  crmLink: (name: string) => `Open CRM record for ${name} (opens in a new tab)`,
} as const;
